import { useState } from 'react';
import type { LabelType } from '@common/customControls/getServiceLabel';
import type { IAuthorization } from '@frontend/components/interfaces';
import type { IProcedureAddEdit } from '@frontend/components/pages/procedures/ProcedureItemAddEdit';
import type { ServiceCode } from '../../../../common/enums/serviceCode';
import type { IProcedureGetOne } from '../../interfaces/procedureGetOne';
import { ToastType } from '../../../../common/enums/toastType';
import { fetchJson } from '../../../store/fetch';
import { translateKey } from '../../../translate';
import { showServiceToast } from '../../../utils/showToast';
import { Button } from '../common';
import { ConfirmationModal } from '../modals/ConfirmationModal';

interface ICancelProcedureButtonProps {
    authorization?: IAuthorization;
    isAutoSaveModalVisible?: boolean;
    onShowAutoSaveModal?: (showModal: boolean) => void;
    procedure: IProcedureGetOne | IProcedureAddEdit;
    removeProcedure?: () => Promise<void>;
    serviceCode: ServiceCode;
    toastServiceLabelType?: LabelType;
}

export function CancelProcedureButton(props: ICancelProcedureButtonProps): JSX.Element {
    const { procedure, removeProcedure, serviceCode } = props;
    const [showModal, setShowModal] = useState(false);

    const authorization = 'authorization' in procedure ? procedure.authorization : props.authorization;

    if (!authorization || !authorization.canUpdateProcedureToCancelled || !removeProcedure) {
        return <></>;
    }

    return (
        <>
            <ConfirmationModal
                handleNo={() => handleSetShowModal(false)}
                handleYes={async () => {
                    await fetchJson(`/api/procedures/${procedure.id}/cancel`, 'POST', {});
                    showServiceToast(
                        ToastType.info,
                        'toast.cancelSuccess',
                        { code: serviceCode },
                        props.toastServiceLabelType || 'procedure',
                        translateKey('toast.cancelDefaultMessage')
                    );
                    await removeProcedure!();
                }}
                isOpen={showModal || !!props.isAutoSaveModalVisible}
                question={translateKey('request.confirmCancelRequest')}
                title={translateKey('common.notice')}
            />

            <Button
                automation="removeProcedure"
                color="secondary"
                large
                onClick={() => handleSetShowModal(true)}
                outlined
            >
                {translateKey('request.statusAction_cancelled')}
            </Button>
        </>
    );

    function handleSetShowModal(isVisible: boolean) {
        if (props.onShowAutoSaveModal) {
            props.onShowAutoSaveModal(isVisible);
        } else {
            setShowModal(isVisible);
        }
    }
}
